import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config';

const mkKey = (categoryId, voucherId) => `voucher:${categoryId}:${voucherId}`;

const parseApiError = async (response, fallback) => {
    const data = await response.json().catch(() => ({}));
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length) {
        return detail.map((x) => x?.msg || JSON.stringify(x)).join('; ');
    }
    if (response.status === 403) return 'Недостаточно прав доступа. Нужно право «Просмотр» в разделе «Цифровые товары».';
    return fallback;
};

const DigitalGoodsPage = ({ onShowToast }) => {
    const [permissions, setPermissions] = useState({});
    const [hydrated, setHydrated] = useState(false);
    const [categoryId, setCategoryId] = useState('');
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(true);
    const [redeemInstruction, setRedeemInstruction] = useState('');
    const [telegramPremiumMarkupPercent, setTelegramPremiumMarkupPercent] = useState('0');
    const [vouchers, setVouchers] = useState([]);
    const [botItems, setBotItems] = useState([]);
    const [loadingVouchers, setLoadingVouchers] = useState(false);
    const [vouchersError, setVouchersError] = useState('');
    const [servicesError, setServicesError] = useState('');
    const [metaError, setMetaError] = useState('');
    const [lastPriceSync, setLastPriceSync] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savingMeta, setSavingMeta] = useState(false);
    const [togglingBot, setTogglingBot] = useState(null);
    const [configMap, setConfigMap] = useState({});

    const botItemIds = useMemo(() => new Set(botItems.map((x) => x.item_id)), [botItems]);

    const canEditDigitalGoods = useMemo(() => {
        const dg = permissions.digital_goods || {};
        return !!(dg.edit || dg.create || dg.delete);
    }, [permissions]);

    const onShowToastRef = useRef(onShowToast);
    useEffect(() => {
        onShowToastRef.current = onShowToast;
    }, [onShowToast]);

    const persistConfigItems = useCallback(async (items) => {
        const r = await fetch(`${API_BASE_URL}/api/settings/digital-goods/config`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || 'Не удалось сохранить конфиг');
        return data;
    }, []);

    const loadBotItems = useCallback(async () => {
        const r = await fetch(`${API_BASE_URL}/api/settings/digital-goods/bot-items`, {
            credentials: 'include',
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || 'Не удалось загрузить товары для бота');
        const items = data.items || [];
        setBotItems(items);
        setConfigMap((prev) => {
            const next = { ...prev };
            for (const item of items) {
                const key = item.item_id;
                const existing = next[key] || {};
                next[key] = {
                    ...existing,
                    ...item,
                    enabled: true,
                    markup_percent: Number(item.markup_percent ?? existing.markup_percent ?? 0),
                    web_markup_percent: Number(
                        item.web_markup_percent != null
                            ? item.web_markup_percent
                            : existing.web_markup_percent != null
                              ? existing.web_markup_percent
                              : item.markup_percent || 0
                    ),
                };
            }
            return next;
        });
        return items;
    }, []);

    const buildItemPayload = (voucher, cfg, cid) => {
        const key = mkKey(cid, voucher.id);
        const botMarkup = Number(cfg.markup_percent ?? 0);
        return {
            item_id: key,
            item_type: 'voucher',
            title: (cfg.title ?? voucher.name ?? key).trim() || key,
            enabled: true,
            markup_percent: botMarkup,
            web_markup_percent: Number(
                cfg.web_markup_percent != null ? cfg.web_markup_percent : botMarkup
            ),
            source_min_price: Number(voucher.minPrice ?? cfg.source_min_price ?? 0),
            instruction: cfg.instruction ?? '',
        };
    };

    const setBotVisibility = useCallback(
        async (voucher, categoryIdForRow, checked, cfgOverride) => {
            const cid = String(categoryIdForRow || '').trim();
            const key = mkKey(cid, voucher.id);
            const cfg = cfgOverride || configMap[key] || {
                item_id: key,
                item_type: 'voucher',
                title: voucher.name || key,
                enabled: false,
                markup_percent: 0,
                web_markup_percent: 0,
                source_min_price: Number(voucher.minPrice || 0),
                instruction: '',
            };
            setTogglingBot(key);
            try {
                if (checked) {
                    const payload = buildItemPayload(voucher, cfg, cid);
                    const r = await fetch(
                        `${API_BASE_URL}/api/settings/digital-goods/bot-items/${encodeURIComponent(key)}`,
                        {
                            method: 'PUT',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                        }
                    );
                    const data = await r.json().catch(() => ({}));
                    if (!r.ok) throw new Error(data.detail || 'Не удалось включить товар в боте');
                    await loadBotItems();
                    onShowToastRef.current?.('Товар сохранён в бот');
                } else {
                    const r = await fetch(
                        `${API_BASE_URL}/api/settings/digital-goods/bot-items/${encodeURIComponent(key)}`,
                        { method: 'DELETE', credentials: 'include' }
                    );
                    if (!r.ok) {
                        const data = await r.json().catch(() => ({}));
                        throw new Error(data.detail || 'Не удалось убрать товар из бота');
                    }
                    setBotItems((prev) => prev.filter((x) => x.item_id !== key));
                    onShowToastRef.current?.('Товар убран из бота');
                }
                setConfigMap((m) => ({
                    ...m,
                    [key]: { ...cfg, enabled: checked, source_min_price: Number(voucher.minPrice || 0) },
                }));
            } catch (e) {
                onShowToastRef.current?.(e.message || 'Ошибка');
            } finally {
                setTogglingBot(null);
            }
        },
        [configMap, loadBotItems]
    );

    const loadVouchers = useCallback(
        async (cat) => {
            const cid = String(cat || '').trim();
            if (!cid) {
                setVouchers([]);
                return;
            }
            setLoadingVouchers(true);
            setVouchersError('');
            try {
                const r = await fetch(
                    `${API_BASE_URL}/api/settings/digital-goods/vouchers/${encodeURIComponent(cid)}`,
                    { credentials: 'include' }
                );
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    let msg =
                        typeof data.detail === 'string'
                            ? data.detail
                            : data.detail?.message ||
                              JSON.stringify(data.detail || '') ||
                              'Не удалось загрузить ваучеры';
                    if (msg.includes('ProductType')) {
                        msg =
                            `WATA не принимает категорию ${cid}. Выберите другую категорию в списке выше ` +
                            'или попросите администратора сохранить правильную категорию. ' +
                            `(WATA: ${msg})`;
                    }
                    throw new Error(msg);
                }
                const items = data.items || [];
                setVouchers(items);

                setConfigMap((prev) => {
                    const next = { ...prev };
                    let changed = false;
                    for (const v of items) {
                        const key = mkKey(cid, v.id);
                        const minP = Number(v.minPrice || 0);
                        const ex = next[key];
                        if (!ex) {
                            next[key] = {
                                item_id: key,
                                item_type: 'voucher',
                                title: String(v.name || key),
                                enabled: false,
                                markup_percent: 0,
                                web_markup_percent: 0,
                                source_min_price: minP,
                                instruction: '',
                            };
                            changed = true;
                        } else if (Number(ex.source_min_price) !== minP) {
                            next[key] = { ...ex, source_min_price: minP };
                            changed = true;
                        }
                    }
                    if (changed && canEditDigitalGoods) {
                        const payload = Object.values(next);
                        persistConfigItems(payload)
                            .then(() => {
                                setLastPriceSync({
                                    ok: true,
                                    at: new Date(),
                                    msg: 'Себестоимость minPrice из WATA записана в настройки.',
                                });
                            })
                            .catch((e) => {
                                setLastPriceSync({
                                    ok: false,
                                    at: new Date(),
                                    msg: e.message || 'Не удалось сохранить обновлённые цены',
                                });
                                onShowToastRef.current?.(e.message || 'Ошибка сохранения цен');
                            });
                    } else {
                        setLastPriceSync(null);
                    }
                    return next;
                });
            } catch (e) {
                const msg = e.message || 'Ошибка загрузки ваучеров';
                setVouchers([]);
                setVouchersError(msg);
                onShowToastRef.current?.(msg);
            } finally {
                setLoadingVouchers(false);
            }
        },
        [persistConfigItems, canEditDigitalGoods]
    );

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .then((me) => {
                if (me?.permissions) setPermissions(me.permissions);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoadingServices(true);
            setServicesError('');
            setMetaError('');
            try {
                const rs = await fetch(
                    `${API_BASE_URL}/api/settings/digital-goods/voucher-services?include_unavailable=true`,
                    { credentials: 'include' }
                );
                let servicesList = [];
                if (!cancelled && rs.ok) {
                    const sd = await rs.json();
                    servicesList = sd.items || [];
                    setServices(servicesList);
                } else if (!cancelled && !rs.ok) {
                    const msg = await parseApiError(rs, 'Не удалось загрузить категории WATA');
                    setServices([]);
                    setServicesError(msg);
                    onShowToastRef.current?.(msg);
                }

                let cat = '';
                let redeem = '';
                let premiumMarkup = '0';
                let metaLoaded = false;
                const rm = await fetch(`${API_BASE_URL}/api/settings/digital-goods/meta`, { credentials: 'include' });
                if (rm.ok && !cancelled) {
                    metaLoaded = true;
                    const meta = await rm.json();
                    if (meta.digital_goods_category_id != null && String(meta.digital_goods_category_id).trim()) {
                        cat = String(meta.digital_goods_category_id).trim();
                    }
                    if (meta.digital_goods_redeem_instruction != null) {
                        redeem = String(meta.digital_goods_redeem_instruction);
                    }
                    if (meta.telegram_premium_markup_percent != null) {
                        premiumMarkup = String(meta.telegram_premium_markup_percent);
                    }
                } else if (!cancelled && !rm.ok) {
                    const msg = await parseApiError(
                        rm,
                        rm.status === 404
                            ? 'Эндпоинт настроек категории не найден — обновите backend.'
                            : 'Не удалось загрузить сохранённую категорию'
                    );
                    setMetaError(msg);
                }
                setRedeemInstruction(redeem);
                setTelegramPremiumMarkupPercent(premiumMarkup);

                const rc = await fetch(`${API_BASE_URL}/api/settings/digital-goods/config`, {
                    credentials: 'include',
                });
                if (rc.ok && !cancelled) {
                    const c = await rc.json();
                    const next = {};
                    for (const item of c.items || []) {
                        next[item.item_id] = { ...item, enabled: !!item.enabled };
                    }
                    setConfigMap(next);
                }

                if (!cancelled) {
                    await loadBotItems();
                }

                if (cancelled) return;

                // Автовыбор только если meta загрузилась, но категория ещё не сохранена в БД.
                // Если meta недоступна (403/404) — не подставляем первую из списка (часто неверный ID).
                if (!cat && servicesList.length && metaLoaded) {
                    const firstAvail = servicesList.find((s) => s.isAvailable) || servicesList[0];
                    cat = firstAvail ? String(firstAvail.id) : '';
                }
                setCategoryId(cat);
                setHydrated(true);
            } catch (e) {
                if (!cancelled) onShowToastRef.current?.(e.message);
                setHydrated(true);
            } finally {
                if (!cancelled) setLoadingServices(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [loadBotItems]);

    useEffect(() => {
        if (!hydrated || !categoryId) return;
        loadVouchers(categoryId);
    }, [hydrated, categoryId, loadVouchers]);

    const serviceNameById = useMemo(() => {
        const m = {};
        for (const s of services) {
            m[String(s.id)] = s.name;
        }
        return m;
    }, [services]);

    const saveCategoryToBot = async (cid) => {
        const r = await fetch(`${API_BASE_URL}/api/settings/digital-goods/meta`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                digital_goods_category_id: String(cid || '').trim(),
                digital_goods_redeem_instruction: redeemInstruction || '',
                telegram_premium_markup_percent: String(telegramPremiumMarkupPercent ?? '0').trim(),
            }),
        });
        if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error(err.detail || 'Не удалось сохранить категорию');
        }
    };

    const onCategorySelect = async (e) => {
        const val = e.target.value;
        setCategoryId(val);
        if (!val) return;
        try {
            setSavingMeta(true);
            await saveCategoryToBot(val);
            onShowToastRef.current?.('Категория сохранена');
        } catch (err) {
            onShowToastRef.current?.(err.message || 'Ошибка');
        } finally {
            setSavingMeta(false);
        }
    };

    const updateVoucherConfig = (voucher, patch) => {
        const key = mkKey(categoryId, voucher.id);
        const prev = configMap[key] || {
            item_id: key,
            item_type: 'voucher',
            title: voucher.name || key,
            enabled: botItemIds.has(key),
            markup_percent: 0,
            web_markup_percent: 0,
            source_min_price: Number(voucher.minPrice || 0),
            instruction: '',
        };
        setConfigMap((m) => ({
            ...m,
            [key]: {
                ...prev,
                ...patch,
                source_min_price: Number(voucher.minPrice || 0),
            },
        }));
    };

    const onBotCheckboxChange = async (voucher, cid, checked) => {
        const key = mkKey(cid, voucher.id);
        const cfg = configMap[key] || {
            item_id: key,
            item_type: 'voucher',
            title: voucher.name || key,
            enabled: false,
            markup_percent: 0,
            web_markup_percent: 0,
            source_min_price: Number(voucher.minPrice || 0),
            instruction: '',
        };
        await setBotVisibility(voucher, cid, checked, cfg);
    };

    const saveConfig = async () => {
        setSaving(true);
        try {
            const items = Object.values(configMap).map((x) => ({
                ...x,
                enabled: botItemIds.has(x.item_id),
            }));
            await persistConfigItems(items);
            onShowToastRef.current?.('Наценки и названия сохранены');
        } catch (e) {
            onShowToastRef.current?.(e.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const saveMeta = async () => {
        setSavingMeta(true);
        try {
            await saveCategoryToBot(categoryId);
            onShowToastRef.current?.('Настройки сохранены');
            await loadVouchers(categoryId);
        } catch (e) {
            onShowToastRef.current?.(e.message || 'Ошибка');
        } finally {
            setSavingMeta(false);
        }
    };

    const calcFinal = (minPrice, markupPercent) => {
        const minP = Number(minPrice || 0);
        let client = minP * (1 + Number(markupPercent || 0) / 100);
        const maxAmt = Math.round(minP * 1.5 * 100) / 100;
        if (client > maxAmt) client = maxAmt;
        return Math.round(client * 100) / 100;
    };

    const voucherByKeyInCategory = useMemo(() => {
        const m = {};
        for (const v of vouchers) {
            m[mkKey(categoryId, v.id)] = v;
        }
        return m;
    }, [vouchers, categoryId]);

    const categoryVouchersNotInBot = vouchers.filter((v) => !botItemIds.has(mkKey(categoryId, v.id)));

    const renderTableRow = (voucher, cid, opts = {}) => {
        const { pinned = false, botRow = null } = opts;
        const key = botRow?.item_id || mkKey(cid, voucher.id);
        const cfg = configMap[key] || {};
        const markup = Number(cfg.markup_percent ?? botRow?.markup_percent ?? 0);
        const minP = Number(voucher?.minPrice ?? cfg.source_min_price ?? botRow?.source_min_price ?? 0);
        const final = calcFinal(minP, markup).toFixed(2);
        const checked = botItemIds.has(key);
        const catLabel = serviceNameById[String(cid)] || `ID ${cid}`;
        return (
            <tr
                key={key}
                className={`border-b border-slate-100 align-top ${pinned ? 'bg-indigo-50/60' : ''}`}
            >
                <td className="px-3 py-2 max-w-[200px]">
                    {pinned && (
                        <span className="block text-[10px] uppercase tracking-wide text-indigo-600 font-semibold mb-1">
                            В боте · {catLabel}
                        </span>
                    )}
                    {voucher?.name || cfg.title || botRow?.title || key}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{minP.toFixed(2)} ₽</td>
                <td className="px-3 py-2">
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={cfg.markup_percent ?? botRow?.markup_percent ?? 0}
                        onChange={(e) =>
                            updateVoucherConfig(
                                voucher || { id: botRow?.voucher_id, minPrice: minP, name: botRow?.title },
                                { markup_percent: Number(e.target.value) || 0 }
                            )
                        }
                        disabled={!canEditDigitalGoods}
                        className="w-24 px-2 py-1 border border-slate-200 rounded disabled:opacity-60"
                    />
                </td>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{final} ₽</td>
                <td className="px-3 py-2">
                    <input
                        value={cfg.title ?? botRow?.title ?? voucher?.name ?? ''}
                        onChange={(e) =>
                            updateVoucherConfig(
                                voucher || { id: botRow?.voucher_id, minPrice: minP, name: botRow?.title },
                                { title: e.target.value }
                            )
                        }
                        disabled={!canEditDigitalGoods}
                        className="w-full min-w-[140px] px-2 py-1 border border-slate-200 rounded text-xs disabled:opacity-60"
                        placeholder={voucher?.name || botRow?.title}
                    />
                </td>
                <td className="px-3 py-2">
                    <textarea
                        value={cfg.instruction ?? botRow?.instruction ?? ''}
                        onChange={(e) =>
                            updateVoucherConfig(
                                voucher || { id: botRow?.voucher_id, minPrice: minP, name: botRow?.title },
                                { instruction: e.target.value }
                            )
                        }
                        disabled={!canEditDigitalGoods}
                        rows={2}
                        className="w-full min-w-[160px] px-2 py-1 border border-slate-200 rounded text-xs disabled:opacity-60"
                        placeholder="К этому товару"
                    />
                </td>
                <td className="px-3 py-2">
                    <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canEditDigitalGoods || togglingBot === key}
                        onChange={(e) => onBotCheckboxChange(
                            voucher || {
                                id: botRow?.voucher_id,
                                minPrice: minP,
                                name: botRow?.title || cfg.title,
                            },
                            String(botRow?.category_id || cid),
                            e.target.checked
                        )}
                    />
                </td>
            </tr>
        );
    };

    const unknownCategory =
        categoryId && !services.some((s) => String(s.id) === String(categoryId));

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Цифровые товары WATA</h2>

            {!canEditDigitalGoods && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    У вас только просмотр раздела. Для создания и редактирования товаров нужно право «Редактирование» в
                    разделе «Цифровые товары».
                </div>
            )}

            <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-4">
                <p className="text-sm text-slate-600">
                    Галочка «В боте» сразу сохраняется в таблицу{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">digital_goods_bot_items</code> — по ней строится
                    каталог в Telegram. Эти товары всегда показаны вверху таблицы ниже, из всех категорий. Наценки и
                    названия — в настройке{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">wata_digital_goods_config</code>.
                </p>
                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                    <div className="flex-1 min-w-[240px]">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                            Категория ваучеров (из WATA DG)
                        </label>
                        {loadingServices ? (
                            <p className="text-sm text-slate-500">Загрузка списка категорий...</p>
                        ) : (
                            <select
                                value={categoryId}
                                onChange={onCategorySelect}
                                disabled={!canEditDigitalGoods || savingMeta || !services.length}
                                className="w-full max-w-xl px-3 py-2 border border-slate-200 rounded-lg bg-white"
                            >
                                <option value="">— Выберите категорию —</option>
                                {services.map((s) => (
                                    <option key={s.id} value={String(s.id)}>
                                        {s.name}
                                        {!s.isAvailable ? ' (недоступна в WATA)' : ''}
                                    </option>
                                ))}
                                {unknownCategory && (
                                    <option value={String(categoryId)}>
                                        Сохранено в базе (ID {categoryId}) — нет в текущем списке API
                                    </option>
                                )}
                            </select>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => categoryId && loadVouchers(categoryId)}
                        disabled={savingMeta || !categoryId || loadingVouchers}
                        className="px-4 py-2 border border-slate-300 rounded-lg text-sm disabled:opacity-60"
                    >
                        Обновить список и цены
                    </button>
                </div>
                <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                        Telegram Premium — наценка к динамической цене WATA, %
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                        Для Premium цена берётся из WATA TopUp в момент загрузки/создания заказа. В боте сначала
                        выбирают срок 3, 6 или 12 месяцев, затем показывается итоговая цена с этой наценкой.
                    </p>
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={telegramPremiumMarkupPercent}
                        onChange={(e) => setTelegramPremiumMarkupPercent(e.target.value)}
                        disabled={!canEditDigitalGoods}
                        className="w-full max-w-xs px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:opacity-60"
                        placeholder="0"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                        Общая инструкция по активации (для всех товаров, дополняет текст у строки)
                    </label>
                    <textarea
                        value={redeemInstruction}
                        onChange={(e) => setRedeemInstruction(e.target.value)}
                        disabled={!canEditDigitalGoods}
                        rows={4}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:opacity-60"
                        placeholder="Например: активируйте код в App Store → ..."
                    />
                    <button
                        type="button"
                        onClick={saveMeta}
                        disabled={!canEditDigitalGoods || savingMeta}
                        className="mt-2 px-4 py-2 bg-slate-700 text-white rounded-lg text-sm disabled:opacity-60"
                    >
                        {savingMeta ? 'Сохранение...' : 'Сохранить инструкцию и категорию'}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                {lastPriceSync && (
                    <div
                        className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
                            lastPriceSync.ok
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                                : 'border-rose-200 bg-rose-50 text-rose-900'
                        }`}
                    >
                        {lastPriceSync.msg}
                        {lastPriceSync.at && (
                            <span className="block text-xs opacity-80 mt-1">
                                {lastPriceSync.at.toLocaleString('ru-RU')}
                            </span>
                        )}
                    </div>
                )}
                {metaError && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {metaError} Выберите категорию вручную в списке выше.
                    </div>
                )}
                {servicesError && (
                    <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                        {servicesError}
                    </div>
                )}
                {vouchersError && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {vouchersError}
                    </div>
                )}
                {loadingVouchers && botItems.length === 0 ? (
                    <div className="text-slate-500">Загрузка ваучеров...</div>
                ) : (
                    <div className="overflow-auto">
                        <table className="min-w-full text-sm">
                            <thead className="border-b bg-slate-50">
                                <tr>
                                    <th className="px-3 py-2 text-left">Название (как в WATA)</th>
                                    <th className="px-3 py-2 text-left">Себестоимость minPrice</th>
                                    <th className="px-3 py-2 text-left">Наценка %</th>
                                    <th className="px-3 py-2 text-left">Цена клиенту</th>
                                    <th className="px-3 py-2 text-left">Отображаемое имя</th>
                                    <th className="px-3 py-2 text-left">Инструкция (опц.)</th>
                                    <th className="px-3 py-2 text-left">В боте</th>
                                </tr>
                            </thead>
                            <tbody>
                                {botItems.length > 0 && (
                                    <>
                                        <tr className="bg-slate-100">
                                            <td colSpan={7} className="px-3 py-2 text-xs font-semibold text-slate-600">
                                                Товары в боте (все категории) — сверху в каталоге Telegram
                                            </td>
                                        </tr>
                                        {botItems.map((botRow) => {
                                            const cid = String(botRow.category_id || '');
                                            const v =
                                                voucherByKeyInCategory[botRow.item_id] || {
                                                    id: botRow.voucher_id,
                                                    name: botRow.title,
                                                    minPrice: botRow.source_min_price,
                                                };
                                            return renderTableRow(v, cid, { pinned: true, botRow });
                                        })}
                                        <tr className="bg-slate-50">
                                            <td colSpan={7} className="px-3 py-2 text-xs font-semibold text-slate-500">
                                                Категория: {serviceNameById[categoryId] || categoryId}
                                            </td>
                                        </tr>
                                    </>
                                )}
                                {categoryVouchersNotInBot.map((v) =>
                                    renderTableRow(v, categoryId, { pinned: false })
                                )}
                            </tbody>
                        </table>
                        {vouchers.length === 0 && botItems.length === 0 && !loadingVouchers && categoryId && (
                            <p className="text-slate-500 text-sm py-6">
                                Нет ваучеров в категории или проверьте токен WATA DG.
                            </p>
                        )}
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={saveConfig}
                disabled={!canEditDigitalGoods || saving}
                className="px-5 py-3 rounded-xl bg-indigo-600 text-white font-medium disabled:opacity-60"
            >
                {saving ? 'Сохранение...' : 'Сохранить наценки и названия'}
            </button>
        </div>
    );
};

export default DigitalGoodsPage;
